import type * as api from "./api";

export const ROLE_TEXT: Record<string, string> = {
  administrator: "Administrator",
  operations_manager: "Operations manager",
  sales_manager: "Sales manager",
  salesperson: "Salesperson",
  dispatcher: "Dispatcher",
  driver: "Driver",
  office: "Office",
  accounting: "Accounting",
  facility_operator: "Facility operator",
  viewer: "Viewer",
};

/** The name of a built-in role, or of a custom role from the names that the API sends. */
export const roleName = (role: string, names: Readonly<Record<string, string>> = {}) => names[role] ?? ROLE_TEXT[role] ?? role.replace(/_/g, " ");

/** One role with its place: "Driver" at the whole organization, else "Driver (North, South)". */
export function roleText(grant: api.RoleGrant, hubs: readonly api.Hub[], names: Readonly<Record<string, string>> = {}): string {
  if (grant.scope === "tenant") return roleName(grant.role, names);
  const places = grant.hub_ids.map((id) => hubs.find((hub) => hub.id === id)?.name ?? "unknown hub");
  return `${roleName(grant.role, names)} (${places.join(", ")})`;
}

export const rolesText = (grants: readonly api.RoleGrant[], hubs: readonly api.Hub[], names: Readonly<Record<string, string>> = {}) =>
  grants.length ? grants.map((grant) => roleText(grant, hubs, names)).join(", ") : "No role";

export function signInText(at: string | null, now: Date = new Date()): string {
  if (!at) return "Never";
  const days = Math.floor((now.getTime() - new Date(at).getTime()) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
}

/** Rule U-11: what is wrong with the roles of the form, or null. The API checks again. */
export function rolesProblem(grants: readonly api.RoleGrant[], names: Readonly<Record<string, string>> = {}): string | null {
  if (!grants.length) return "Select one role or more.";
  const seen = new Set<string>();
  for (const grant of grants) {
    if (grant.scope === "hub" && !grant.hub_ids.length) return `Select a hub for ${roleName(grant.role, names)}.`;
    const key = `${grant.role}/${grant.scope}`;
    if (seen.has(key)) return `${roleName(grant.role, names)} is in the list two times.`;
    seen.add(key);
  }
  return null;
}

export function inviteProblem(email: string, grants: readonly api.RoleGrant[], names: Readonly<Record<string, string>> = {}): string | null {
  const clean = email.trim();
  if (!clean.includes("@") || /\s/.test(clean)) return "Enter an email address.";
  return rolesProblem(grants, names);
}

/** The person must type the email before a deactivation, so a wrong row is not removed by a click. */
export const confirmMatches = (typed: string, email: string) => typed.trim().toLowerCase() === email.toLowerCase();

export const isAdminGrant = (grants: readonly api.RoleGrant[]) => grants.some((g) => g.role === "administrator" && g.scope === "tenant");

/** Rule V-11: the preferred truck of a driver. An inactive truck is not used. */
export function preferredTruckText(member: Pick<api.Member, "preferred_truck" | "preferred_truck_id">, trucks: readonly api.MemberTruck[] = []): string {
  if (!member.preferred_truck) return "None";
  const inactive = trucks.some((truck) => truck.id === member.preferred_truck_id && !truck.active);
  return inactive ? `${member.preferred_truck} (inactive, not used)` : member.preferred_truck;
}

/** The trucks a driver can prefer: the active trucks, and the current one. */
export const truckChoices = (trucks: readonly api.MemberTruck[], current: string | null) => trucks.filter((truck) => truck.active || truck.id === current);

type Outcome = { ok: boolean; text: string };

export function deactivatedText(reply: { sign_in_disabled?: boolean }): Outcome {
  if (reply.sign_in_disabled === false) {
    return { ok: false, text: "Deactivated: the member has no role and sees no data. But the sign-in is not turned off. Send an email to developers@resourceoil.com." };
  }
  return { ok: true, text: "Deactivated. The member has no role and cannot sign in." };
}

export function reactivatedText(reply: { sign_in_enabled?: boolean }): Outcome {
  if (reply.sign_in_enabled === false) {
    return { ok: false, text: "Active again, but the sign-in is not turned on. The member cannot sign in yet. Send an email to developers@resourceoil.com." };
  }
  return { ok: true, text: "Active again. The member can sign in." };
}
